import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { submissions, users, products as productsApi, brokers as brokersApi, documents } from '../services/api.js';
// Chat widget removed — replaced by co-pilot analysis on canvas
import { getRiskBadgeClass, getRiskLabel, RISK_TIER_OPTIONS } from '../utils/riskUtils';
import { AccountList } from './AccountList.jsx';
import { ReviewSummary } from './ReviewSummary.jsx';
import './UnderwriterDashboard.css';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getSubId(sub) {
  return sub.SubmissionID || sub.submissionId || sub.submission_id || sub.id;
}

function getSubStatus(sub) {
  return (sub.Status || sub.status || '').toLowerCase();
}

function getSubAgeHours(sub) {
  const created = sub.CreatedAt || sub.createdAt || sub.created_at;
  if (!created) return Infinity;
  return (Date.now() - new Date(created).getTime()) / (1000 * 60 * 60);
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'bound', label: 'Bound' },
  { value: 'declined', label: 'Declined' },
  { value: 'referred', label: 'Referred' },
  { value: 'agent_reviewed', label: 'Agent Reviewed' },
  { value: 'data_extracted', label: 'Data Extracted' },
  { value: 'awaiting_info', label: 'Awaiting Info' },
  { value: 'dismissed', label: 'Dismissed' },
];

const RISK_TIER_FILTER_OPTIONS = [
  { value: '', label: 'All Risk Tiers' },
  ...RISK_TIER_OPTIONS,
];

const LEFT_PANEL_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'referred', label: 'Referred' },
  { value: 'awaiting_info', label: 'Awaiting Info' },
];

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2, none: 3, '': 3 };
const PRIORITY_CYCLE = ['none', 'high', 'medium', 'low'];

export function UnderwriterDashboard() {
  const [hubTab, setHubTab] = useState('queue');
  const navigate = useNavigate();
  const { user, hasGroup } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productList, setProductList] = useState([]);
  const [uwList, setUwList] = useState([]);
  const [brokerList, setBrokerList] = useState([]);

  const isLeadership = hasGroup('leadership');
  const [viewAsUW, setViewAsUW] = useState(null);

  const [filterProduct, setFilterProduct] = useState('');
  const [filterRiskTier, setFilterRiskTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUw, setFilterUw] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const [workload, setWorkload] = useState(null);
  const [priorities, setPriorities] = useState({});

  const [leftFilter, setLeftFilter] = useState({ type: null, value: null });

  const cyclePriority = async (id) => {
    const current = priorities[id] || 'none';
    const idx = PRIORITY_CYCLE.indexOf(current);
    const next = PRIORITY_CYCLE[(idx + 1) % PRIORITY_CYCLE.length];
    setPriorities((prev) => ({ ...prev, [id]: next }));
    try {
      await submissions.update(id, { priority: next });
    } catch { /* best effort */ }
  };

  const toggleLeftFilter = (type, value) => {
    setLeftFilter((prev) =>
      prev.type === type && prev.value === value
        ? { type: null, value: null }
        : { type, value }
    );
  };

  useEffect(() => {
    setLeftFilter({ type: null, value: null });
  }, [filterProduct, filterRiskTier, filterStatus, filterUw]);

  const [showNewForm, setShowNewForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [newInsuredName, setNewInsuredName] = useState('');
  const [newProductId, setNewProductId] = useState('');
  const [newBrokerId, setNewBrokerId] = useState('');
  const uploadInputRef = React.useRef(null);

  useEffect(() => {
    if (productList.length > 0 && !newProductId) {
      setNewProductId(productList[0]?.ProductID || productList[0]?.productId || '');
    }
    if (brokerList.length > 0 && !newBrokerId) {
      setNewBrokerId(brokerList[0]?.BrokerID || brokerList[0]?.brokerId || '');
    }
  }, [productList, brokerList]);

  const handleUploadSubmission = async () => {
    if (!uploadFile) return;
    setUploadError('');

    if (!newInsuredName.trim()) {
      setUploadError('Insured name is required.');
      return;
    }
    if (!newProductId) {
      setUploadError('Please select a product.');
      return;
    }

    setUploading(true);
    setUploadStatus('Creating submission...');
    try {
      const createResult = await submissions.create({
        insuredName: newInsuredName.trim(),
        productId: newProductId,
        brokerId: newBrokerId,
        source: 'upload',
      });
      const raw = createResult.data || createResult;
      const subData = raw.data || raw;
      const subId = subData.submissionId || subData.SubmissionID;
      const accountId = subData.accountId || subData.AccountID;

      if (!subId) throw new Error('No submission ID returned');

      setUploadStatus('Uploading application...');
      const docResult = await documents.upload({
        accountId,
        submissionId: subId,
        fileName: uploadFile.name,
        fileType: uploadFile.type || 'application/pdf',
        fileSize: uploadFile.size,
        docType: 'application',
      });
      const docRaw = docResult.data || docResult;
      const docData = docRaw.data || docRaw;

      const s3Response = await fetch(docData.uploadUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': uploadFile.type || 'application/pdf',
          'x-amz-server-side-encryption': 'AES256',
        },
      });
      if (!s3Response.ok) {
        throw new Error(`File upload to S3 failed (${s3Response.status}). Please try again.`);
      }

      setUploadStatus('Processing application...');
      await documents.process(docData.documentId);

      setUploadStatus('Opening underwriting canvas...');
      await new Promise((r) => setTimeout(r, 1500));

      setShowNewForm(false);
      setUploadFile(null);
      setUploadStatus('');
      setNewInsuredName('');
      navigate(`/underwriting/${subId}`);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadStatus('');
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterUw) params.assignedUW = filterUw;

      const result = await submissions.list(params);
      let items = result.data || result.submissions || result.items || [];

      // Client-side filters for fields the backend doesn't support
      if (filterProduct) {
        items = items.filter((s) => (s.ProductID || s.productId || '') === filterProduct);
      }
      if (filterRiskTier) {
        items = items.filter((s) => (s.RiskTier || s.riskTier || '') === filterRiskTier);
      }

      setData(items);

      const prioMap = {};
      (Array.isArray(items) ? items : []).forEach((s) => {
        const id = getSubId(s);
        const sd = s.StructuredData || s.structuredData || {};
        prioMap[id] = s.Priority || s.priority || sd.priority || 'none';
      });
      setPriorities((prev) => ({ ...prev, ...prioMap }));
    } catch (err) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filterProduct, filterStatus, filterUw, filterRiskTier]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  useEffect(() => {
    async function loadFilters() {
      try {
        const [prods, uwUsers, brokersRes] = await Promise.allSettled([
          productsApi.list(),
          users.list({ role: 'underwriter' }),
          brokersApi.list(),
        ]);
        if (prods.status === 'fulfilled') {
          const items = prods.value.data || prods.value.products || prods.value.items || [];
          setProductList(Array.isArray(items) ? items : []);
        }
        if (uwUsers.status === 'fulfilled') {
          const items = uwUsers.value.data || uwUsers.value.users || uwUsers.value.items || [];
          setUwList(Array.isArray(items) ? items : []);
        }
        if (brokersRes.status === 'fulfilled') {
          const items = brokersRes.value.data || brokersRes.value || [];
          setBrokerList(Array.isArray(items) ? items : []);
        }
      } catch {
        // Filters are non-critical
      }
    }
    loadFilters();
  }, []);

  useEffect(() => {
    async function loadWorkload() {
      if (!user?.userId) return;
      try {
        const result = await users.getWorkload(user.userId);
        setWorkload(result.data || result);
      } catch {
        // Non-critical
      }
    }
    loadWorkload();
  }, [user?.userId]);

  const currentCount = workload?.current || workload?.active_count || 0;
  const maxCount = workload?.max || workload?.capacity || 25;
  const capacityPct = Math.min((currentCount / maxCount) * 100, 100);
  const capacityColor =
    capacityPct >= 90 ? 'var(--color-risk-red)' :
    capacityPct >= 70 ? 'var(--color-risk-orange)' :
    capacityPct >= 50 ? 'var(--color-risk-yellow)' :
    'var(--color-risk-green)';

  const stats = useMemo(() => {
    const items = Array.isArray(data) ? data : [];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const boundCount = items.filter(s => getSubStatus(s) === 'bound').length;
    const quotedCount = items.filter(s => getSubStatus(s) === 'quoted').length;
    const declinedCount = items.filter(s => getSubStatus(s) === 'declined').length;

    const hitDenom = boundCount + quotedCount + declinedCount;
    const hitRatio = hitDenom > 0 ? Math.round((boundCount / hitDenom) * 100) : 0;

    const todayCount = items.filter(s => {
      const d = new Date(s.CreatedAt || s.createdAt || s.created_at || 0);
      return d >= startOfToday;
    }).length;

    return { boundCount, quotedCount, hitRatio, hitDenom, todayCount };
  }, [data]);

  const panelCounts = useMemo(() => {
    const items = Array.isArray(data) ? data : [];

    const statusCounts = {};
    items.forEach(s => {
      const st = getSubStatus(s);
      statusCounts[st] = (statusCounts[st] || 0) + 1;
    });

    const priorityCounts = { high: 0, medium: 0 };
    items.forEach(s => {
      const id = getSubId(s);
      const p = priorities[id] || 'none';
      if (p === 'high') priorityCounts.high++;
      if (p === 'medium') priorityCounts.medium++;
    });

    const agingCounts = { '0-24': 0, '24-48': 0, '48+': 0 };
    items.forEach(s => {
      const hours = getSubAgeHours(s);
      if (hours <= 24) agingCounts['0-24']++;
      else if (hours <= 48) agingCounts['24-48']++;
      else agingCounts['48+']++;
    });

    const touchedCount = items.filter(s => getSubStatus(s) !== 'new').length;
    const decisionsCount = items.filter(s =>
      ['bound', 'declined', 'referred'].includes(getSubStatus(s))
    ).length;

    return { statusCounts, priorityCounts, agingCounts, touchedCount, decisionsCount };
  }, [data, priorities]);

  return (
    <div className="uw-dashboard">
      {/* Hub Tab Bar */}
      <div className="hub-tabs">
        <button className={`hub-tab ${hubTab === 'queue' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('queue')}>Submission Queue</button>
        <button className={`hub-tab ${hubTab === 'accounts' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('accounts')}>Accounts</button>
        <button className={`hub-tab ${hubTab === 'reviews' ? 'hub-tab--active' : ''}`} onClick={() => setHubTab('reviews')}>Reviews</button>
      </div>

      {hubTab === 'accounts' && <AccountList />}
      {hubTab === 'reviews' && <ReviewSummary />}
      {hubTab === 'queue' && (
      <>
      {/* Performance Stats Header */}
      <div className="uw-dashboard__stats">
        <div className="uw-stat-card">
          <span className="uw-stat-card__value">{stats.boundCount}</span>
          <span className="uw-stat-card__label">MTD Bound</span>
        </div>
        <div className="uw-stat-card">
          <span className="uw-stat-card__value">{stats.quotedCount}</span>
          <span className="uw-stat-card__label">MTD Quoted</span>
        </div>
        <div className="uw-stat-card">
          <span className="uw-stat-card__value">{stats.hitDenom > 0 ? `${stats.hitRatio}%` : '—'}</span>
          <span className="uw-stat-card__label">Hit Ratio</span>
        </div>
        <div className="uw-stat-card">
          <span className="uw-stat-card__value">{stats.todayCount}</span>
          <span className="uw-stat-card__label">Submissions Today</span>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="uw-dashboard__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <div>
            <h1 className="uw-dashboard__title">Submission Queue</h1>
            <p className="uw-dashboard__subtitle">
              {Array.isArray(data) ? data.length : 0} submission{data.length !== 1 ? 's' : ''}
              {viewAsUW && <span style={{ marginLeft: 8, fontWeight: 600 }}> (viewing as: {uwList.find(u => (u.UnderwriterID || u.underwriterId) === viewAsUW)?.Name || uwList.find(u => (u.UnderwriterID || u.underwriterId) === viewAsUW)?.name || viewAsUW})</span>}
            </p>
          </div>
          <button
            className="btn btn--primary"
            onClick={() => setShowNewForm(!showNewForm)}
          >
            + New Submission
          </button>
          {isLeadership && uwList.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', background: 'var(--color-bg-elevated, var(--color-surface-elevated))', borderRadius: 8, padding: '6px 12px' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, opacity: 0.7 }}>View as:</span>
              <button
                className={`btn btn--sm ${!viewAsUW ? 'btn--primary' : ''}`}
                onClick={() => { setViewAsUW(null); setFilterUw(''); }}
                style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              >
                All
              </button>
              {uwList.map((uw) => {
                const uwId = uw.UnderwriterID || uw.underwriterId;
                const uwName = (uw.Name || uw.name || '').split(' ')[0];
                return (
                  <button
                    key={uwId}
                    className={`btn btn--sm ${viewAsUW === uwId ? 'btn--primary' : ''}`}
                    onClick={() => { setViewAsUW(uwId); setFilterUw(uwId); }}
                    style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                  >
                    {uwName}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="uw-dashboard__workload card">
          <div className="workload__label">
            <span className="workload__title">My Workload</span>
            <span className="workload__count">{currentCount} / {maxCount}</span>
          </div>
          <div className="workload__bar">
            <div
              className="workload__fill"
              style={{ width: `${capacityPct}%`, backgroundColor: capacityColor }}
            />
          </div>
          <span className="workload__hint">
            {maxCount - currentCount} slots available
          </span>
        </div>
      </div>

      {showNewForm && (
        <div className="card" style={{ marginBottom: 'var(--space-md)', padding: 'var(--space-lg)' }}>
          <h3 style={{ marginBottom: 'var(--space-sm)', fontSize: '16px' }}>New Submission</h3>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-md)' }}>
            Upload a carrier application and the Engine will extract the data, identify the format, and create the submission automatically.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Insured Name *</label>
              <input
                type="text"
                className="pom__search"
                placeholder="e.g. Acme Corp"
                value={newInsuredName}
                onChange={(e) => { setNewInsuredName(e.target.value); setUploadError(''); }}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Product *</label>
              <select
                className="pom__filter-select"
                value={newProductId}
                onChange={(e) => setNewProductId(e.target.value)}
                style={{ width: '100%' }}
              >
                {productList.length === 0 && <option value="">Loading products...</option>}
                {productList.map((p) => {
                  const id = p.ProductID || p.productId;
                  const name = p.Name || p.name || id;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Broker</label>
              <select
                className="pom__filter-select"
                value={newBrokerId}
                onChange={(e) => setNewBrokerId(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">No broker</option>
                {brokerList.map((b) => {
                  const id = b.BrokerID || b.brokerId;
                  const name = b.Name || b.name || b.CompanyName || b.companyName || id;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </div>
          </div>

          <div
            style={{
              border: '2px dashed var(--color-border)',
              borderRadius: 'var(--radius-md, 8px)',
              padding: '32px',
              textAlign: 'center',
              cursor: 'pointer',
              background: uploadFile ? 'var(--color-primary-light)' : 'transparent',
              transition: 'background 0.2s',
            }}
            onClick={() => uploadInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = 'var(--color-primary-light)'; }}
            onDragLeave={(e) => { e.currentTarget.style.background = uploadFile ? 'var(--color-primary-light)' : 'transparent'; }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.background = 'var(--color-primary-light)';
              const file = e.dataTransfer.files[0];
              if (file) {
                setUploadFile(file);
                if (!newInsuredName) {
                  setNewInsuredName(file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
                }
              }
            }}
          >
            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.tiff"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setUploadFile(file);
                  if (!newInsuredName) {
                    setNewInsuredName(file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
                  }
                }
              }}
            />
            {uploadFile ? (
              <div>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#128196;</div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{uploadFile.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                  {(uploadFile.size / 1024).toFixed(0)} KB &middot; Click to change
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>&#128228;</div>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>Drop application here or click to browse</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                  PDF, DOCX, XLSX, CSV, images
                </div>
              </div>
            )}
          </div>

          {uploadError && (
            <div style={{ marginTop: 'var(--space-sm)', fontSize: '13px', color: 'var(--color-risk-red, #ef4444)', fontWeight: 600 }}>
              {uploadError}
            </div>
          )}
          {uploadStatus && (
            <div style={{ marginTop: 'var(--space-sm)', fontSize: '13px', color: 'var(--color-primary)', fontWeight: 600 }}>
              {uploadStatus}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-md)' }}>
            <button
              className="btn btn--primary"
              onClick={handleUploadSubmission}
              disabled={uploading || !uploadFile}
            >
              {uploading ? 'Processing...' : 'Upload & Process'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => { setShowNewForm(false); setUploadFile(null); setUploadStatus(''); setUploadError(''); setNewInsuredName(''); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="uw-dashboard__error">
          <p>{error}</p>
          <button className="btn btn--secondary" onClick={loadSubmissions}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="uw-dashboard__loading">
          <div className="spinner" />
          <p>Loading submissions...</p>
        </div>
      ) : (
        <div className="uw-dashboard__panels">
          {/* Left Panel — My Work */}
          <aside className="uw-dashboard__left-panel">
            <div className="uw-left-section">
              <h3 className="uw-left-section__title">Assigned Submissions</h3>
              {LEFT_PANEL_STATUSES.map(({ value, label }) => (
                <button
                  key={value}
                  className={`uw-left-count${leftFilter.type === 'status' && leftFilter.value === value ? ' uw-left-count--active' : ''}`}
                  onClick={() => toggleLeftFilter('status', value)}
                >
                  <span className="uw-left-count__label">{label}</span>
                  <span className="uw-left-count__value">{panelCounts.statusCounts[value] || 0}</span>
                </button>
              ))}
            </div>

            <div className="uw-left-section">
              <h3 className="uw-left-section__title">Priority Flags</h3>
              <button
                className={`uw-left-count${leftFilter.type === 'priority' && leftFilter.value === 'high' ? ' uw-left-count--active' : ''}`}
                onClick={() => toggleLeftFilter('priority', 'high')}
              >
                <span className="uw-left-count__label">High Priority</span>
                <span className="uw-left-count__value uw-left-count__value--high">{panelCounts.priorityCounts.high}</span>
              </button>
              <button
                className={`uw-left-count${leftFilter.type === 'priority' && leftFilter.value === 'medium' ? ' uw-left-count--active' : ''}`}
                onClick={() => toggleLeftFilter('priority', 'medium')}
              >
                <span className="uw-left-count__label">Medium Priority</span>
                <span className="uw-left-count__value uw-left-count__value--medium">{panelCounts.priorityCounts.medium}</span>
              </button>
            </div>

            <div className="uw-left-section">
              <h3 className="uw-left-section__title">Today&rsquo;s Activity</h3>
              <div className="uw-activity-stat">
                <span className="uw-activity-stat__label">Submissions Touched</span>
                <span className="uw-activity-stat__value">{panelCounts.touchedCount}</span>
              </div>
              <div className="uw-activity-stat">
                <span className="uw-activity-stat__label">Decisions Made</span>
                <span className="uw-activity-stat__value">{panelCounts.decisionsCount}</span>
              </div>
            </div>
          </aside>

          {/* Right Panel — Submission List */}
          <main className="uw-dashboard__right-panel">
            <div className="uw-dashboard__filters card">
              <select
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className="uw-filter__select"
              >
                <option value="">All Products</option>
                {productList.map((p) => (
                  <option key={p.ProductID || p.productId || p.id} value={p.ProductID || p.productId || p.id}>
                    {p.Name || p.name || p.product_name}
                  </option>
                ))}
              </select>

              <select
                value={filterRiskTier}
                onChange={(e) => setFilterRiskTier(e.target.value)}
                className="uw-filter__select"
              >
                {RISK_TIER_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="uw-filter__select"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              <select
                value={filterUw}
                onChange={(e) => setFilterUw(e.target.value)}
                className="uw-filter__select"
              >
                <option value="">All Underwriters</option>
                {uwList.map((u) => (
                  <option key={u.UnderwriterID || u.underwriterId || u.id} value={u.UnderwriterID || u.underwriterId || u.id}>
                    {u.Name || u.name || u.display_name || u.Email || u.email}
                  </option>
                ))}
              </select>

              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="uw-filter__select"
              >
                <option value="">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <button className="btn btn--secondary" onClick={loadSubmissions}>
                Refresh
              </button>

              {leftFilter.type && (
                <button
                  className="btn btn--sm uw-left-clear-btn"
                  onClick={() => setLeftFilter({ type: null, value: null })}
                >
                  Clear: {leftFilter.value.replace(/_/g, ' ')} &times;
                </button>
              )}
            </div>

            {/* Review Progress */}
            {Array.isArray(data) && data.length > 0 && (() => {
              const total = data.length;
              const inReview = data.filter((s) => getSubStatus(s) === 'in_review').length;
              const completed = data.filter((s) => {
                const st = getSubStatus(s);
                return st === 'bound' || st === 'declined' || st === 'referred';
              }).length;
              const remaining = total - inReview - completed;
              const pct = Math.round((completed / total) * 100);
              return (
                <div className="uw-progress-bar card">
                  <span className="uw-progress-bar__label">Review Progress:</span>
                  <span className="uw-progress-bar__detail">
                    {inReview} in review, {completed} completed, {remaining} remaining
                  </span>
                  <div className="uw-progress-bar__track">
                    <div
                      className="uw-progress-bar__fill"
                      style={{
                        width: `${pct}%`,
                        background: pct === 100 ? 'var(--color-risk-green, #22c55e)' : 'var(--color-primary, #3b82f6)',
                      }}
                    />
                  </div>
                  <span className="uw-progress-bar__count">{completed} / {total}</span>
                </div>
              );
            })()}

            {/* Submission Table */}
            <div className="uw-dashboard__table-wrap card">
              <table className="uw-table">
                <thead>
                  <tr>
                    <th>Insured</th>
                    <th>Industry</th>
                    <th>Priority</th>
                    <th>Risk Score</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let rows = Array.isArray(data) ? [...data] : [];

                    if (filterStatus !== 'dismissed' && !leftFilter.value) {
                      rows = rows.filter((s) => getSubStatus(s) !== 'dismissed');
                    }

                    if (filterPriority) {
                      rows = rows.filter((s) => {
                        const id = getSubId(s);
                        return (priorities[id] || 'none') === filterPriority;
                      });
                    }

                    if (leftFilter.type === 'status') {
                      rows = rows.filter(s => getSubStatus(s) === leftFilter.value);
                    }
                    if (leftFilter.type === 'priority') {
                      rows = rows.filter(s => {
                        const id = getSubId(s);
                        return (priorities[id] || 'none') === leftFilter.value;
                      });
                    }
                    if (leftFilter.type === 'age') {
                      rows = rows.filter(s => {
                        const hours = getSubAgeHours(s);
                        if (leftFilter.value === '0-24') return hours <= 24;
                        if (leftFilter.value === '24-48') return hours > 24 && hours <= 48;
                        return hours > 48;
                      });
                    }

                    rows.sort((a, b) => {
                      const idA = getSubId(a);
                      const idB = getSubId(b);
                      const prioA = PRIORITY_ORDER[priorities[idA] || 'none'] ?? 3;
                      const prioB = PRIORITY_ORDER[priorities[idB] || 'none'] ?? 3;
                      if (prioA !== prioB) return prioA - prioB;
                      const dateA = new Date(a.CreatedAt || a.createdAt || a.created_at || 0);
                      const dateB = new Date(b.CreatedAt || b.createdAt || b.created_at || 0);
                      return dateB - dateA;
                    });

                    if (rows.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} className="uw-table__empty">
                            No submissions found matching your filters.
                          </td>
                        </tr>
                      );
                    }

                    return rows.map((sub) => {
                      const id = getSubId(sub);
                      const riskScore = sub.RiskScore ?? sub.riskScore ?? sub.risk_score;
                      const status = sub.Status || sub.status || 'new';
                      const isReviewed = ['bound', 'declined', 'referred'].includes(status);
                      const insuredName = sub.InsuredName || sub.insuredName || '—';
                      const sd = sub.StructuredData || sub.structuredData || {};
                      const industry = sd.industry || sub.Industry || sub.industry || '';
                      const priority = priorities[id] || 'none';
                      return (
                        <tr
                          key={id}
                          className={`uw-table__row ${isReviewed ? 'uw-table__row--reviewed' : ''} ${status === 'dismissed' ? 'uw-table__row--dismissed' : ''}`}
                          onClick={() => navigate(`/underwriting/${id}`)}
                        >
                          <td className="uw-table__insured">
                            {isReviewed && <span style={{ marginRight: 6, color: 'var(--color-risk-green, #22c55e)' }}>&#10003;</span>}
                            {insuredName}
                          </td>
                          <td style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>{industry || '—'}</td>
                          <td>
                            <span
                              className="uw-priority-badge"
                              data-priority={priority}
                              onClick={(e) => { e.stopPropagation(); cyclePriority(id); }}
                              title={`Priority: ${priority || 'none'} (click to change)`}
                            >
                              {priority === 'high' ? '!!!' : priority === 'medium' ? '!!' : priority === 'low' ? '!' : '—'}
                            </span>
                          </td>
                          <td>
                            {riskScore != null && riskScore > 0 ? (
                              <span className={getRiskBadgeClass(riskScore)}>
                                {riskScore} · {getRiskLabel(riskScore)}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-tertiary, #94a3b8)', fontSize: '13px' }}>Not scored</span>
                            )}
                          </td>
                          <td>
                            <span className="uw-status-chip" data-status={status}>
                              {status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="uw-table__date">
                            {formatDate(sub.CreatedAt || sub.createdAt || sub.created_at)}
                          </td>
                          <td className="uw-table__actions">
                            <button
                              className="uw-dismiss-btn"
                              title={status === 'dismissed' ? 'Restore submission' : 'Dismiss as junk/duplicate'}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await submissions.dismiss(id);
                                  loadSubmissions();
                                } catch (err) {
                                  console.error('Dismiss failed:', err);
                                }
                              }}
                            >
                              {status === 'dismissed' ? '↩' : '⊘'}
                            </button>
                            <button
                              className="uw-delete-btn"
                              title="Delete submission"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete submission for ${insuredName}?`)) return;
                                try {
                                  await submissions.delete(id);
                                  loadSubmissions();
                                } catch (err) {
                                  alert('Failed to delete: ' + (err.message || 'Unknown error'));
                                }
                              }}
                            >
                              &times;
                            </button>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </main>
        </div>
      )}
      </>
      )}
    </div>
  );
}
